import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@components/input';
import { InputSelectNestedCheckbox } from '@components/input-select-nested-checkbox';
import { LabelPrimary } from '@components/label';
import { getChildrenIds } from '@components/nested-checkbox-list/utils';
import { Select, SelectOption } from '@components/select';
import { TeamChannelSection, type ValidationError } from '@components/team-channel-section';
import { collectPartiallySelectedTeamIds, filterTreeToSelected, mergeTreeCaps } from '@utils/channel-tree';
import { formatEventTeamsToNestedCheckboxList } from '@utils/format-event-teams-to-nested-checkbox-list';

import {
  CHANNEL_QUOTA_ALL,
  CHANNEL_QUOTA_PER_CHANNEL,
  CHANNEL_QUOTA_UNLIMITED,
  type ChannelQuotaMode,
  type DistributionChannelLimitValue,
} from '../distribution-section.type';

interface ChannelLimitFieldsProps {
  value: DistributionChannelLimitValue;
  onChange: (next: DistributionChannelLimitValue) => void;
  channelErrors?: ValidationError[];
  channelLimitError?: string;
  disabled?: boolean;
  labelClassName?: string;
  dataTestId: string;
}

/**
 * Legacy "Sales channels + Sales quota" controls re-expressed on the new team/channel tree: pick the
 * channels the offer is on, then choose the quota mode (none / per-channel / one shared cap).
 */
export const ChannelLimitFields = ({
  value,
  onChange,
  channelErrors,
  channelLimitError,
  disabled = false,
  labelClassName,
  dataTestId,
}: ChannelLimitFieldsProps): JSX.Element => {
  const { t } = useTranslation();
  const [limitTouched, setLimitTouched] = useState(false);

  const nestedData = useMemo(() => formatEventTeamsToNestedCheckboxList(value.channels), [value.channels]);

  const selectedIdsWithParents = useMemo(() => {
    const selected = new Set(value.selectedChannelIds);
    const ids = [...value.selectedChannelIds];
    nestedData.forEach((item) => {
      if (item.children.length > 0 && item.children.every((child) => selected.has(child.id))) ids.push(item.id);
    });
    return ids;
  }, [value.selectedChannelIds, nestedData]);

  const hasSelection = value.selectedChannelIds.length > 0;

  const partiallySelectedTeamIds = useMemo(
    () => collectPartiallySelectedTeamIds(value.channels, value.selectedChannelIds),
    [value.channels, value.selectedChannelIds]
  );

  return (
    <div className='flex flex-col gap-5' data-testid={dataTestId}>
      <div className='flex flex-col gap-2'>
        <LabelPrimary htmlFor={`${dataTestId}-channels`} marginBottomClassName=''>
          {t('offers:admissionTicketSalesChannelLabel')}
        </LabelPrimary>
        <InputSelectNestedCheckbox
          id={`${dataTestId}-channels`}
          data={nestedData}
          selectedIds={selectedIdsWithParents}
          onChange={(ids) => onChange({ ...value, selectedChannelIds: getChildrenIds(ids, nestedData) })}
          emptySelectText={t('offers:admissionTicketSalesChannelsNoneSelected')}
          canSelectAll
          selectAllText={t('offers:selectAllLabel')}
          selectAllId={`${dataTestId}-select-all`}
          canSearch
          searchText={t('offers:admissionTicketSalesChannelsSearchPlaceholder')}
          clearable
          clearText={t('offers:deselectAllLabel')}
          disabled={disabled}
        />
      </div>

      {hasSelection && (
        <div className='flex flex-col gap-2'>
          <LabelPrimary htmlFor={`${dataTestId}-quota`} marginBottomClassName=''>
            {t('offers:admissionTicketSalesQuotaLabel')}
          </LabelPrimary>
          <Select
            id={`${dataTestId}-quota`}
            value={value.quotaMode}
            onChange={(event) => onChange({ ...value, quotaMode: event.currentTarget.value as ChannelQuotaMode })}
            disabled={disabled}>
            <SelectOption
              value={CHANNEL_QUOTA_UNLIMITED}
              label={t('offers:admissionTicketSalesQuotaUnlimitedOption')}
            />
            <SelectOption
              value={CHANNEL_QUOTA_PER_CHANNEL}
              label={t('offers:admissionTicketSalesQuotaLimitByChannelOption')}
            />
            <SelectOption
              value={CHANNEL_QUOTA_ALL}
              label={t('offers:admissionTicketSalesQuotaLimitChannelAllOption')}
            />
          </Select>
        </div>
      )}

      {hasSelection && value.quotaMode === CHANNEL_QUOTA_PER_CHANNEL && (
        <TeamChannelSection
          value={filterTreeToSelected(value.channels, value.selectedChannelIds)}
          onChange={(edited) => onChange({ ...value, channels: mergeTreeCaps(value.channels, edited) })}
          errors={channelErrors}
          canCloseSales={false}
          labelClassName={labelClassName}
          partiallySelectedTeamIds={partiallySelectedTeamIds}
        />
      )}

      {hasSelection && value.quotaMode === CHANNEL_QUOTA_ALL && (
        <div className='flex flex-col gap-2'>
          <LabelPrimary htmlFor={`${dataTestId}-limit-all`} marginBottomClassName=''>
            {t('offers:admissionTicketSalesQuotaLimitByChannelLabel')}
          </LabelPrimary>
          <div className='w-48'>
            <Input
              id={`${dataTestId}-limit-all`}
              type='number'
              step={1}
              min='1'
              value={value.limitAllChannels}
              onChange={(event) => {
                setLimitTouched(true);
                onChange({ ...value, limitAllChannels: event.currentTarget.value });
              }}
              hasError={limitTouched && !!channelLimitError}
              disabled={disabled}
            />
          </div>
          {limitTouched && channelLimitError && <p className='text-body-s text-critical'>{channelLimitError}</p>}
        </div>
      )}
    </div>
  );
};
